import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { allSoftware, formatDownloads, sortSoftware } from "@/utils/software";
import type { Software } from "@/utils/software";
import RatingStars from "@/components/base/RatingStars";

interface Props {
  onDownload: (item: Software) => void;
}

const featured = sortSoftware(allSoftware, "rating").slice(0, 4);

export default function FeaturedCarousel({ onDownload }: Props) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (featured.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 5200);
    return () => clearInterval(timer);
  }, []);

  if (featured.length === 0) return null;

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + featured.length) % featured.length);
  };

  return (
    <section className="card-surface overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-4">
        <div className="flex items-center gap-2">
          <i className="ri-fire-line text-lg text-accent-500" />
          <h2 className="font-heading text-lg font-bold text-foreground-900">焦点推荐</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="上一个"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="下一个"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>

      <div className="relative">
        {featured.map((item, i) => (
          <article
            key={item.id}
            className={`grid gap-0 md:grid-cols-2 ${i === index ? "animate-fade-up" : "hidden"}`}
          >
            <Link
              to={`/detail/${item.id}`}
              className="relative block h-[200px] w-full overflow-hidden bg-secondary-900 md:h-full md:min-h-[260px]"
            >
              <img
                src={item.cover}
                alt={`${item.name}软件界面截图`}
                title={`${item.name} 黄金投资软件推荐`}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-4 top-4 chip bg-secondary-900/80 text-primary-200 backdrop-blur-sm">
                {item.categoryName}
              </span>
            </Link>
            <div className="flex flex-col gap-4 p-5 md:p-6">
              <div className="flex items-center gap-2">
                <span className="chip bg-accent-100 text-accent-800">
                  <i className="ri-trophy-line" />
                  编辑推荐第 {i + 1} 位
                </span>
              </div>
              <div>
                <Link
                  to={`/detail/${item.id}`}
                  className="font-heading text-xl font-bold text-foreground-900 transition-colors hover:text-primary-700 md:text-2xl"
                >
                  {item.name}
                </Link>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.intro}</p>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-price-tag-3-line" />v{item.version}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-download-2-line" />
                  {formatDownloads(item.downloads)}次下载
                </span>
                <RatingStars rating={item.rating} />
              </div>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span key={tag} className="chip bg-secondary-100 text-secondary-800">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => onDownload(item)}
                  className="btn-accent text-sm"
                >
                  <i className="ri-download-2-line" />免费下载
                </button>
                <Link to={`/detail/${item.id}`} className="btn-ghost text-sm">
                  查看详情 <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="flex items-center justify-center gap-2 border-t border-background-200 py-3">
        {featured.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 个推荐`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              i === index ? "w-6 bg-primary-500" : "w-2 bg-background-300"
            }`}
          />
        ))}
      </div>
    </section>
  );
}